import { Box, Card, Grid2, Typography, Button, alpha, Avatar, Stack } from '@mui/material';
import { useEffect, useState } from 'react';
import { getFacilitiyCertificates } from '../../../../helpers/api';
import { openNotification } from '../../../../helpers/notification';
import LoaderSkeleton from '../../../common/LoaderSkeleton';
import { Empty } from 'antd';
import { DownloadRounded, PictureAsPdfRounded, VerifiedRounded } from '@mui/icons-material';

export default function FacilityCertificates({ props, facilityId }) {
  const [certList, setCertList] = useState([]);
  const [certLoading, setCertLoading] = useState(false);

  const getQuarterTitle = (val) => {
    const titles = {
      1: 'First Quarter Report',
      2: 'Second Quarter Report',
      3: 'Third Quarter Report',
      4: 'Fourth Quarter Report',
    };
    return titles[val] || `Quarter ${val} Report`;
  };

  useEffect(() => {
    if (facilityId) fetchCerts();
  }, [facilityId]);

  const fetchCerts = async () => {
    setCertLoading(true);
    try {
      const res = await getFacilitiyCertificates(facilityId);
      const { status, data } = res?.result;
      if (status?.code === '00') {
        setCertList(data || []);
      } else {
        openNotification({ type: 'error', message: status?.desc });
      }
    } catch (error) {
      const errmsg = error?.data?.result?.data?.status?.desc || 'Unable to fetch certificates';
      openNotification({ type: 'error', message: errmsg });
    } finally {
      setCertLoading(false);
    }
  };

  const renderContent = () => {
    if (certLoading) {
      return (
        <Grid2 container spacing={2}>
          {[1, 2].map((n) => (
            <Grid2 key={n} size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
              <LoaderSkeleton height={120} />
            </Grid2>
          ))}
        </Grid2>
      );
    }

    if (!certList || certList.length < 1) {
      return (
        <Box
          sx={{ py: 10, textAlign: 'center', bgcolor: alpha('#f4f6f8', 0.5), borderRadius: '16px' }}
        >
          <Empty description="No compliance certificates found" />
        </Box>
      );
    }

    return (
      <Grid2 container spacing={3}>
        {certList.map((i, index) => (
          <Grid2 key={index} size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
            <Card
              elevation={0}
              sx={{
                p: 2,
                borderRadius: '16px',
                border: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderColor: 'primary.main',
                  boxShadow: '0px 8px 24px rgba(0,0,0,0.05)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              {/* Document Icon Box */}
              <Avatar
                variant="rounded"
                sx={{
                  width: 70,
                  height: 70,
                  bgcolor: alpha('#f44336', 0.1),
                  color: '#f44336',
                  border: '1px solid',
                  borderColor: alpha('#f44336', 0.2),
                }}
              >
                <PictureAsPdfRounded sx={{ fontSize: 35 }} />
              </Avatar>

              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                  <Typography variant="subtitle1" fontWeight={700} noWrap>
                    {getQuarterTitle(i?.reportQuarter)}
                  </Typography>
                  <VerifiedRounded sx={{ fontSize: 16, color: 'success.main' }} />
                </Stack>

                <Typography variant="body2" color="text.secondary" fontWeight={600} mb={1.5}>
                  QAQC: {i?.reportYear}
                </Typography>

                <Button
                  component="a"
                  href={i?.certString}
                  target="_blank"
                  download={`Certificate_${i?.reportYear}_Q${i?.reportQuarter}.pdf`}
                  size="small"
                  variant="contained"
                  disableElevation
                  startIcon={<DownloadRounded />}
                  sx={{
                    textTransform: 'none',
                    borderRadius: '8px',
                    fontWeight: 700,
                    bgcolor: alpha('#1976d2', 0.1),
                    color: 'primary.main',
                    px: 2,
                    '&:hover': {
                      bgcolor: 'primary.main',
                      color: '#fff',
                    },
                  }}
                >
                  Download Certificate
                </Button>
              </Box>
            </Card>
          </Grid2>
        ))}
      </Grid2>
    );
  };

  return (
    <Box>
      <Box mb={2}>
        <Typography variant="h6" fontWeight={700} color="text.primary" sx={{ lineHeight: 1.2 }}>
          Compliance Documents
        </Typography>
        <Typography variant="body2" color="textSecondary">
          View and download officially approved monitoring certificates
        </Typography>
      </Box>
      {renderContent()}
    </Box>
  );
}
